/* global ol */

var GoogleMapsLayer = require('./googleMapsLayer');
var PickupLocationLayer = require('./pickupLocationLayer');
var RouteLayer = require('./routeLayer');
var CarLayer = require('./carLayer');
var SelectInteractionStyle = require('./selectInteractionStyle');
var HoverRouteInteraction = require('./hoverRouteInteraction');
var DoubleClickZoomInteraction = require('./doubleClickZoomInteraction');
var MoveStopsInteraction = require('./moveStopsInteraction');
var EditRouteInteraction = require('./editRouteInteraction');
var Tooltip = require('./tooltip');
var MapTooltipTemplate = require('./mapTooltipTemplate');
var SelectRouteControl = require('./selectRouteControl');
var FullScreenControl = require('./fullScreenControl');
var SaturationControl = require('./saturationControl');
var RouteZoomControl = require('./routeZoomControl');
var MouseCursor = require('./mouseCursor');
var MapState = require('./mapState');

var maxZoom = 20;
var minZoom = 4;

function Map(scope, $rootScope, $location, $compile, modal, storage, clock, routeId, routeSequence) {
  this.scope = scope;
  this.scope.map = {
    selectRoute: this.initSelectRoute($location)
  };

  this.view = new ol.View({
    maxZoom: maxZoom,
    minZoom: minZoom
  });

  this.map = new ol.Map({
    target: 'openLayers',
    view: this.view,
    interactions: this.initInteractions(),
    controls: []
  });

  this.canEditRoute = scope.canEditRouteInfo(scope.routePlan);
  this.canMoveStops = scope.canMoveStops && scope.canMoveStops(scope.routePlan);

  this.googleMapsLayer = new GoogleMapsLayer(this.map);

  this.addControls($compile, storage);
  this.addPickupLocationLayers();
  this.addRouteLayers(routeId, routeSequence);
  this.addSelectInteractionStyle();
  this.addHoverRouteInteraction(false);
  this.addDoubleClickZoomInteraction();
  this.addTooltip($compile);

  if (this.scope.routePlan.published) {
    this.addCarLayers(clock);
    this.addHoverRouteInteraction(true);
    this.addEditRouteInteraction();
    new MouseCursor(this.map, this.canEditRoute);
  } else {
    this.addMoveStopsInteraction(modal);
    new MouseCursor(this.map, this.canMoveStops);
  }

  this.addPusherListeners($rootScope, clock);

  this.fitMap($rootScope, storage, routeId);
}

Map.prototype.initSelectRoute = function($location) {
  var control = new SelectRouteControl(this, $location);

  return control.selectRoute.bind(control);
};

Map.prototype.initInteractions = function() {
  return [
    new ol.interaction.DragPan({
      kinetic: false
    }),
    new ol.interaction.DragZoom({
      duration: 0
    }),
    new ol.interaction.MouseWheelZoom({
      constrainResolution: true, // zoom to an integer
      duration: 750
    })
  ];
};

Map.prototype.addControls = function($compile, storage) {
  this.map.addControl(new ol.control.Zoom({
    duration: 0
  }));

  this.fullScreenControl = new FullScreenControl(this.map);
  this.map.addControl(this.fullScreenControl.control);

  this.saturationControl = new SaturationControl(this.googleMapsLayer, storage);
  this.map.addControl(this.saturationControl.control);

  this.routeZoomControl = new RouteZoomControl(this, $compile);
  this.map.addControl(this.routeZoomControl.control);
};

Map.prototype.addPickupLocationLayers = function() {
  this.pickupLocationLayers = {};

  this.scope.routePlan.pickupLocations.forEach(function(location) {
    var layer = new PickupLocationLayer(location);

    this.pickupLocationLayers[location.id] = layer;
    this.map.addLayer(layer.layer);
  }, this);
};

Map.prototype.addRouteLayers = function(routeId, routeSequence) {
  this.routeLayers = {};

  this.scope.routePlan.routes.forEach(function(route) {
    var routeSequenceArgument = routeId === route.id ? routeSequence : null;
    var layer = new RouteLayer(route, this.scope.routeColors[route.color], routeSequenceArgument);

    this.routeLayers[route.id] = layer;
    this.map.addLayer(layer.layer);
  }, this);
};

Map.prototype.addCarLayers = function(clock) {
  this.carLayers = {};

  this.scope.routePlan.routes.forEach(function(route) {
    var layer = new CarLayer(route, clock);

    this.carLayers[route.id] = layer;
    this.map.addLayer(layer.layer);
    this.map.addOverlay(layer.overlay);
  }, this);
};

Map.prototype.addSelectInteractionStyle = function() {
  this.selectStyle = new SelectInteractionStyle(this.map);
  this.map.addInteraction(this.selectStyle.interaction);
};

Map.prototype.addHoverRouteInteraction = function(isCar) {
  var layers = isCar ? this.carLayers : this.routeLayers;

  this.scope.routePlan.routes.forEach(function(route) {
    var layer = layers[route.id];
    var interaction = new HoverRouteInteraction(this, layer, isCar);

    this.map.addInteraction(interaction.interaction);
    layer.hoverInteraction = interaction;
  }, this);
};

Map.prototype.addDoubleClickZoomInteraction = function() {
  this.map.addInteraction(new DoubleClickZoomInteraction(this).interaction);
};

Map.prototype.addEditRouteInteraction = function() {
  if (this.canEditRoute) {
    this.map.addInteraction(new EditRouteInteraction(this).interaction);
  }
};

Map.prototype.addMoveStopsInteraction = function(modal) {
  if (this.canMoveStops) {
    this.moveStopsInteraction = new MoveStopsInteraction(this, modal);
    this.map.addInteraction(this.moveStopsInteraction.dragInteraction);
    this.map.addInteraction(this.moveStopsInteraction.clickInteraction);
  }
};

Map.prototype.addTooltip = function($compile) {
  var mapTooltipTemplate = new MapTooltipTemplate(this.scope, $compile);

  this.tooltip = new Tooltip(mapTooltipTemplate, this.map);
  this.map.addOverlay(this.tooltip.overlay);
  this.map.addInteraction(this.tooltip.interaction);
};

Map.prototype.addPusherListeners = function($rootScope, clock) {
  var deregister = [
    $rootScope.$on('deliveryStatusUpdated', this.handleDeliveryStatusUpdated.bind(this)),
    $rootScope.$on('routeUpdated', this.handleRouteUpdated.bind(this)),
    $rootScope.$on('routeSequenceUpdated', this.handleRouteSequenceUpdated.bind(this)),
    $rootScope.$on('driverPositionUpdated', this.handleDriverPositionUpdated.bind(this)),
    $rootScope.$on('published', this.handlePublished.bind(this, clock))
  ];

  this.scope.$on('$destroy', function() {
    deregister.forEach(function(deregister) {
      deregister();
    });
  });
};

Map.prototype.handleDeliveryStatusUpdated = function(event, delivery) {
  var routeId = this.scope.routePlan.findCustomer(delivery.customerId).routeId;

  this.routeLayers[routeId].redraw();
  this.selectStyle.redrawStyle();
};

Map.prototype.handleRouteUpdated = function(event, routeId) {
  var route = this.scope.routePlan.findRoute(routeId);
  var routeLayer = this.routeLayers[routeId];
  var carLayer = this.carLayers && this.carLayers[routeId];

  if (routeLayer)
    routeLayer.updateColor(this.scope.routeColors[route.color]);

  if (carLayer) {
    carLayer.updateClass();
    carLayer.updatePositionAndVisibility();
  }

  this.selectStyle.redrawStyle();
};

Map.prototype.handleRouteSequenceUpdated = function(event, routeId) {
  var routeLayer = this.routeLayers[routeId];
  if (routeLayer)
    routeLayer.redraw();

  this.selectStyle.redrawStyle();
};

Map.prototype.handleDriverPositionUpdated = function(event, route) {
  if (this.carLayers) {
    this.carLayers[route.id].updatePositionAndVisibility();
  }
};

Map.prototype.handlePublished = function(clock) {
  this.addCarLayers(clock);
  this.addHoverRouteInteraction(true);

  if (this.moveStopsInteraction) {
    this.map.removeInteraction(this.moveStopsInteraction.dragInteraction);
    this.map.removeInteraction(this.moveStopsInteraction.clickInteraction);
  }
  this.addEditRouteInteraction();
};

Map.prototype.fitMap = function($rootScope, storage, routeId) {
  var route = routeId && this.scope.routePlan.findRoute(routeId);

  if (route) {
    this.scope.map.selectRoute(route);
    this.routeZoomControl.zoomToFitRoute();
  }

  if (! this.view.getCenter()) {
    this.routeZoomControl.zoomToFitAllRoutes();
  }

  if (! this.view.getCenter()) {
    this.view.setZoom(5);
    this.view.setCenter(ol.proj.fromLonLat([10.743587, 59.915043]));
  }

  if (this.scope.routePlan.published) {
    this.restoreMapState($rootScope, storage);
  }
};

// Save the map state when going to the customers or dashboard page and
// restore it when coming back
Map.prototype.restoreMapState = function($rootScope, storage) {
  var mapState = new MapState(this.view, this.scope, storage);

  var deregister = $rootScope.$on('$routeChangeStart', function(event, next) {
    if (next.page.indexOf('fleet') === 0) {
      mapState.save();
    } else {
      mapState.remove();
    }
    deregister();
  });

  mapState.restore();
  mapState.remove();
};

Map.prototype.layers = function(layerType) {
  return this.scope.values(this[layerType]).map(function(layer) {
    return layer.layer;
  });
};

module.exports = Map;



/*****************
 ** WEBPACK FOOTER
 ** ./src/js/lib/map/map.js
 ** module id = 181
 ** module chunks = 1
 **/